import { Pager } from "@/app/Pager";
import { Fragment, useState, type ReactElement } from "react";
import { useCanWrite } from "@/app/auth";
import { ChannelRemoveModal } from "@/app/ChannelRemoveModal";
import { ClearFilters } from "@/app/ClearFilters";
import { adjustedHint, formatRate, formatTPS, type ChannelHealth as Row } from "@/app/health";
import { filterRows, GROUP_DIMS, groupRows, parseGroupBy, nextSort, sortViews, type ChannelOf, type HealthFilters, type HealthSort, type HealthView, type SortKey } from "@/app/healthView";
import { channels, suppliers, type Channel } from "@/app/master";
import { loadingOf, nameOf, useChannelHealth, useList } from "@/app/queries";
import { ShowPicker } from "@/app/ShowPicker";
import { ToolIcon } from "@/app/toolIcons";
import { useShown } from "@/app/useShown";
import { useSearchFilters } from "@/app/useSearchFilters";
import { usePage } from "@/app/usePage";
import { Alert, CHANNEL_STATE, ChannelStatePill, Combobox, Dropdown, EmptyState, FILTER_W, formatTime, PageHeader, Pill, Select, Table, TableWrap, Th, Toolbar, ToolbarCount, type ColWidth } from "@/ui";

const FILTER_KEYS = ["supplier", "channel", "mcc", "mnc", "state"] as const;

const DIM_LABEL: Record<(typeof GROUP_DIMS)[number], string> = { channel: "通道", mcc: "MCC", op: "MCC+MNC" };
const DIM_OPTIONS = GROUP_DIMS.map((d) => ({ value: d, label: DIM_LABEL[d] }));

/** 表格可显示的列，按表格里的顺序；“显示列”里勾选显示哪些，缺省全显示，操作列固定、吸收剩余宽度（#541，决策 D199）。sort 是可排序列的排序键。 */
const HEALTH_COLUMNS = [
  { key: "channel", label: "通道", w: 220 }, { key: "op", label: "运营商", w: 136 }, { key: "state", label: "状态", w: 200 },
  { key: "tps", label: "调度 TPS", w: 112, sort: "tps" }, { key: "recentDelivery", label: "5 分钟送达率", w: 120, sort: "recent.delivery" },
  { key: "recentError", label: "5 分钟提交错误率", w: 136, sort: "recent.error" }, { key: "hourDelivery", label: "1 小时送达率", w: 120, sort: "hour.delivery" },
  { key: "hourSamples", label: "1 小时样本", w: 112, sort: "hour.samples" }, { key: "adjusted", label: "修正送达率", w: 112, sort: "adjusted" },
  { key: "latency", label: "回执延迟", w: 104, sort: "latency" }, { key: "since", label: "状态起始", w: "time" },
] as const satisfies readonly { key: string; label: string; w: ColWidth; sort?: SortKey }[];

/** 通道健康表格的列键。 */
type HealthColumn = (typeof HEALTH_COLUMNS)[number]["key"];
const COLUMN_KEYS = HEALTH_COLUMNS.map((c) => c.key);

const STATE_OPTIONS = [{ value: "", label: "全部状态" }, ...Object.entries(CHANNEL_STATE).map(([value, m]) => ({ value, label: m.label }))];

/** 去重后排好的值。 */
const distinct = (xs: string[]) => [...new Set(xs)].sort();

/** 选项里总留着当前选中的值：换了供应商或 MCC、或地址栏带来的值不在快照里时，条件仍看得见、能清掉。 */
const withSelected = (xs: string[], selected: string) => (selected && !xs.includes(selected) ? [...xs, selected] : xs);

/** 通道健康：metrics 每 10 秒建一次的指标快照，按通道 × 运营商看两个窗口的送达率、修正送达率（D97）、提交错误率、熔断状态与慢启动的调度 TPS（决策 D41、D48、D89），可直接摘除或恢复通道。
 * 可按供应商、通道、运营商、状态筛选，按通道或运营商聚合，指标列可排序（决策 D186）；筛选与聚合在地址栏里，刷新后还在。 */
export function ChannelHealth() {
  const canWrite = useCanWrite(); // 只读账号不显示写操作，服务端同样拒绝（docs/04 第 9 节）
  const [removing, setRemoving] = useState<Channel | null>(null);
  const [sort, setSort] = useState<HealthSort>(null);
  const { filters, set, clear, active } = useSearchFilters(FILTER_KEYS);
  const [columns, setColumns] = useShown<HealthColumn>("columns", "channel-health", COLUMN_KEYS, COLUMN_KEYS);
  const groupBy = useSearchFilters(["by"]);
  const dims = parseGroupBy(groupBy.filters.by);
  const q = useChannelHealth();
  const channelList = useList("channels", channels);
  const byId = new Map((channelList.data ?? []).map((c) => [c.id, c]));
  const channelOf: ChannelOf = (id) => (channelList.data ? (byId.get(id) ?? null) : undefined);
  const items = q.data?.items ?? [];
  const shown = filterRows(items, filters, channelOf);
  const views = sortViews(groupRows(shown, dims, channelOf), sort);
  const paged = usePage("channel-health", views, [filters, dims, sort]);
  const tripped = shown.filter((r) => r.circuit === "open" || r.circuit === "half_open").length;
  const limited = shown.filter((r) => r.circuit === "limited").length;
  // 维度总数在分页栏；工具栏只提示筛选范围内异常的数量
  const notes = [tripped && `${tripped} 个熔断或半开`, limited && `${limited} 个 TPS 受限`].filter(Boolean).join("，");
  const header = (c: (typeof HEALTH_COLUMNS)[number]) => "sort" in c
    ? <Th key={c.key} w={c.w} numeric sortable sorted={sort?.key === c.sort && (sort.desc ? "desc" : "asc")} onClick={() => setSort((s) => nextSort(s, c.sort))}>{c.label}</Th>
    : <Th key={c.key} w={c.w}>{c.label}</Th>;

  return (
    <>
      <PageHeader title="通道健康" description="路由用的同一份指标快照。修正送达率只比较同一账户在各通道的结果，按质量打分用它；熔断与慢启动自动推进，手动摘除立即生效。" />
      {q.error && <Alert tone="danger" title="加载失败">{q.error.message}</Alert>}
      {q.data && !q.data.built_at && <Alert tone="warning" title="没有指标快照">metrics 角色没有在运行：路由拿不到指标，熔断也不会推进（决策 D48）。</Alert>}
      <Toolbar actions={<>
        <ShowPicker label="显示列" icon={<ToolIcon name="columns-3" />} heading="显示的列" options={[...HEALTH_COLUMNS]} shown={columns} onChange={setColumns} />
        {active && <ClearFilters onClear={clear} />}
      </>} right={<>{notes && <ToolbarCount>{notes}</ToolbarCount>}{q.data?.built_at && <ToolbarCount>快照 {formatTime(q.data.built_at)}</ToolbarCount>}</>}>
        <HealthFilterBar items={items} filters={filters} set={set} channelOf={channelOf} channelList={channelList.data} />
        <Combobox multiple searchable={false} size="sm" width={FILTER_W.select} ariaLabel="聚合" placeholder="不聚合" options={DIM_OPTIONS} value={dims} onChange={(v) => groupBy.set("by", parseGroupBy(v.join(",")).join(","))} />
      </Toolbar>
      <TableWrap loading={loadingOf(q)}>
        {views.length ? (
          <>
            <Table>
              <thead>
                <tr>{HEALTH_COLUMNS.filter((c) => columns.includes(c.key)).map(header)}<Th /></tr>
              </thead>
              <tbody>
                {paged.rows.map((v) => {
                  const c = v.channelIds.length === 1 ? channelOf(v.channelIds[0]) : null;
                  return <HealthRow key={v.key} v={v} columns={columns} channelName={(id) => nameOf(channelList.data, id)} action={canWrite && c ? () => setRemoving(c) : undefined} removed={!!c?.manually_removed} />;
                })}
              </tbody>
            </Table>
            <Pager {...paged} />
          </>
        ) : (
          !q.isPending && <EmptyState title={items.length ? "没有符合筛选条件的维度" : "没有指标"}>{items.length ? "" : "通道有流量后，metrics 在下一次刷新时建出指标。"}</EmptyState>
        )}
      </TableWrap>
      {removing && <ChannelRemoveModal row={removing} onClose={() => setRemoving(null)} />}
    </>
  );
}

/** 筛选栏：选项只列快照里出现过的（加上当前选中的值）；选了供应商时通道只列它的，选了 MCC 时 MNC 只列这个 MCC 下的。 */
function HealthFilterBar({ items, filters, set, channelOf, channelList }: {
  items: Row[]; filters: HealthFilters; set: (k: keyof HealthFilters, v: string) => void; channelOf: ChannelOf; channelList: Channel[] | undefined;
}) {
  const supplierList = useList("suppliers", suppliers);
  const ids = [...new Set(items.map((r) => r.channel_id))];
  const supplierIds = withSelected([...new Set(ids.map((id) => channelOf(id)?.supplier_id).filter((x) => x !== undefined).map(String))], filters.supplier);
  const channelIds = withSelected(ids.filter((id) => !filters.supplier || channelOf(id)?.supplier_id === Number(filters.supplier)).map(String), filters.channel);
  const mcc = withSelected(distinct(items.map((r) => r.mcc)), filters.mcc);
  const mnc = withSelected(distinct(items.filter((r) => !filters.mcc || r.mcc === filters.mcc).map((r) => r.mnc)), filters.mnc);
  const codes = (xs: string[]) => xs.map((m) => ({ value: m, label: m }));
  return (
    <>
      <Combobox size="sm" width={FILTER_W.select} ariaLabel="供应商" searchPlaceholder="搜索供应商" value={filters.supplier} onChange={(v) => set("supplier", v)}
        options={[{ value: "", label: "全部供应商" }, ...supplierIds.map((id) => ({ value: id, label: nameOf(supplierList.data, Number(id)), hint: `#${id}` }))]} />
      <Combobox size="sm" width={FILTER_W.select} ariaLabel="通道" searchPlaceholder="搜索通道" value={filters.channel} onChange={(v) => set("channel", v)}
        options={[{ value: "", label: "全部通道" }, ...channelIds.map((id) => ({ value: id, label: nameOf(channelList, Number(id)), hint: `#${id}` }))]} />
      <Combobox size="sm" width={FILTER_W.codePick} ariaLabel="MCC" searchPlaceholder="搜索 MCC" value={filters.mcc} onChange={(v) => set("mcc", v)} options={[{ value: "", label: "全部 MCC" }, ...codes(mcc)]} />
      <Combobox size="sm" width={FILTER_W.codePick} ariaLabel="MNC" searchPlaceholder="搜索 MNC" value={filters.mnc} onChange={(v) => set("mnc", v)} options={[{ value: "", label: "全部 MNC" }, ...codes(mnc)]} />
      <Select size="sm" width={FILTER_W.select} ariaLabel="状态" options={STATE_OPTIONS} value={filters.state} onChange={(v) => set("state", v)} />
    </>
  );
}

/** 运营商列：一个时写 mcc-mnc；同一 MCC 的几个写 MCC 与 MNC 个数；跨 MCC 只写个数。 */
function opsLabel(ops: string[]) {
  if (ops.length === 1) return ops[0];
  const mccs = new Set(ops.map((o) => o.split("-")[0]));
  return mccs.size === 1 ? <>{[...mccs][0]} <span className="ax-secondary">{ops.length} 个 MNC</span></> : `${ops.length} 个运营商`;
}

/** 表格的一行。只有一个维度时与原来一样；聚合行的状态按维度计数，调度 TPS 只写实际值之和，状态起始取最差的熔断状态的起始时刻；只画显示的列（#541）。
 * action 为空时不显示摘除与恢复（只读账号，或涉及多条通道）。 */
function HealthRow({ v, columns, channelName, action, removed }: { v: HealthView; columns: readonly HealthColumn[]; channelName: (id: number) => string; action?: () => void; removed: boolean }) {
  const { stats: s } = v;
  const single = v.rows.length === 1 ? v.rows[0] : null;
  const cells: Record<HealthColumn, ReactElement> = {
    channel: <td>{v.channelIds.length === 1 ? <>{channelName(v.channelIds[0])} <span className="ax-mono">#{v.channelIds[0]}</span></> : `${v.channelIds.length} 个通道`}</td>,
    op: <td className="ax-mono">{opsLabel(v.ops)}</td>,
    state: (
      <td>
        {single ? <ChannelStatePill state={v.states[0][0]} /> : v.states.map(([st, n]) => <Pill key={st} tone={CHANNEL_STATE[st].tone}>{CHANNEL_STATE[st].label} {n}</Pill>)}
        {v.limited > 0 && <> <Pill tone="warning" plain>TPS 受限</Pill></>}
      </td>
    ),
    tps: <td className="ax-num" title={single?.circuit === "limited" ? "限额 / 近 1 分钟实际" : "近 1 分钟实际"}>{single ? formatTPS(single) : s.tps.toFixed(1)}</td>,
    recentDelivery: <td className="ax-num">{formatRate(s.recent.delivery_rate, s.recent.sample_size)}</td>,
    recentError: <td className="ax-num">{formatRate(s.recent.submit_error_rate, s.recent.submit_samples)}</td>,
    hourDelivery: <td className="ax-num">{formatRate(s.hour.delivery_rate, s.hour.sample_size)}</td>,
    hourSamples: <td className="ax-num">{s.hour.sample_size}</td>,
    // 同一账户在各通道之间比较后的送达率，个别账户的坏流量不拉低通道（决策 D97）；按质量打分用它，与原始送达率对不上是正常的
    adjusted: <td className="ax-num" title={adjustedHint(s.adjusted)}>{s.adjusted ? formatRate(s.adjusted.delivery_rate, s.adjusted.samples) : "—"}</td>,
    latency: <td className="ax-num">{s.hour.dlr_latency_ms ? `${(s.hour.dlr_latency_ms / 1000).toFixed(1)} 秒` : "—"}</td>,
    since: <td className="ax-mono">{s.circuit_since ? formatTime(s.circuit_since) : "—"}</td>,
  };
  return (
    <tr>
      {columns.map((k) => <Fragment key={k}>{cells[k]}</Fragment>)}
      <td>
        {action && (
          <Dropdown iconOnly label="更多操作" variant="ghost" size="sm" align="end" items={[
            removed ? { key: "restore", label: "恢复通道", onSelect: action } : { key: "remove", label: "摘除通道", danger: true, onSelect: action },
          ]} />
        )}
      </td>
    </tr>
  );
}
